import React, { useState } from 'react';
import { Edit, SimpleForm, useCreatePath, TextInput, useRedirect, useNotify } from 'react-admin';
import { Container, Typography } from '@mui/material';
import { get } from 'lodash';
import { Theme } from 'src/Theme';
import { editContainerStyles } from 'src/styles';
import { ChildFormFields, NOTIFICATION_OPTIONS_SUCCESS, getDeactivationSuccessMessage } from 'src/constants';
import { childrenUtils } from 'src/utils';
import { ShsReturnButton, AutoSaveFormListener, ShsSuccessNotification } from 'src/components/common';
import { useFormAutoSave } from 'src/hooks/useFormAutoSave';
import { AUTOSAVE_RESOURCES } from 'src/constants/autoSave';
import AddToolbar from './Toolbar';
import { PersonalData, EducationInstitutionData, VisitorsList, EducationChildData } from './FieldGroups';
import { CancelEditModal } from '../modals';
import { ValidationErrorModal } from '../modals/ValidationErrorModal';

/**
 * Component for editing a child's profile
 */
export const ChildEdit: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isValidationErrorModalOpen, setValidationErrorModalOpen] = useState(false);
    const createPath = useCreatePath();
    const notify = useNotify();
    const redirect = useRedirect();
    const { storageKey, clearAutoSave, markAsSubmitting, record } = useFormAutoSave(AUTOSAVE_RESOURCES.CHILD);

    /**
     * Opens the cancel edit modal and clears auto-save
     */
    const handleOpenModal = () => {
        clearAutoSave();
        setIsModalOpen(true);
    };

    /**
     * Closes the cancel edit modal
     */
    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    /**
     * Redirects to the child's profile page after successful edit
     */
    const handleRedirect = () => {
        redirect(createPath({ resource: `children/${record.id}/show`, type: 'show' }));
    };

    /**
     * Callback function that is executed on successful edit
     * Displays a success notification and redirects to the child's profile
     */
    const onSuccess = (options?: { isDeactivation?: boolean }) => {
        markAsSubmitting();
        clearAutoSave();
        const notificationMessage = options?.isDeactivation
            ? getDeactivationSuccessMessage(record.firstName, record.lastName)
            : 'Редагування дитини пройшло успішно';
        notify(<ShsSuccessNotification message={notificationMessage} />, NOTIFICATION_OPTIONS_SUCCESS);
        handleRedirect();
    };
    const isChildRecordActive = childrenUtils.isChildEducationActive(
        get(record, 'visit.servicesAgreementDate'),
        get(record, 'visit.servicesEndDate')
    );

    /**
     * Handles form validation error
     * Opens validation error modal
     */
    const handleValidationError = () => {
        setValidationErrorModalOpen(true);
        setIsModalOpen(false);
    };

    /**
     * Closes the validation error modal
     */
    const handleCloseValidationErrorModal = () => {
        setValidationErrorModalOpen(false);
    };

    return (
        <Container sx={editContainerStyles} maxWidth={false}>
            <ShsReturnButton
                label="Назад до профіля дитини"
                sx={{ fontSize: Theme.typography.body2.fontSize, color: Theme.palette.primary.grayBluish }}
                onClick={handleOpenModal}
            />

            <Edit
                mutationMode="pessimistic"
                mutationOptions={{ onSuccess: () => onSuccess() }}
                redirect="show"
                actions={false}>
                <Typography fontSize={'40px'} mb={4} fontWeight="700" color={Theme.palette.primary.midnightBlue}>
                    {isChildRecordActive ? 'Редагувати дитину' : 'Активувати дитину'}
                </Typography>
                <SimpleForm
                    mode="onBlur"
                    reValidateMode="onChange"
                    toolbar={
                        <AddToolbar
                            handleCancel={handleRedirect}
                            editMode
                            isChildRecordActive={isChildRecordActive}
                            onSuccess={onSuccess}
                        />
                    }>
                    <TextInput sx={{ display: 'none' }} source={ChildFormFields.ID} />
                    {storageKey && <AutoSaveFormListener storageKey={storageKey} />}
                    <TextInput sx={{ display: 'none' }} source={ChildFormFields.VERSION} />

                    <TextInput sx={{ display: 'none' }} source={`${ChildFormFields.VISIT}.${ChildFormFields.ID}`} />
                    <TextInput
                        sx={{ display: 'none' }}
                        source={`${ChildFormFields.VISIT}.${ChildFormFields.VERSION}`}
                    />

                    <PersonalData />
                    <EducationInstitutionData />
                    <VisitorsList />
                    <EducationChildData />

                    {isModalOpen && (
                        <CancelEditModal
                            isOpen={isModalOpen}
                            isEdit={true}
                            onCancel={handleRedirect}
                            onClose={handleCloseModal}
                            handleValidationError={handleValidationError}
                            isChildRecordActive={isChildRecordActive}
                        />
                    )}
                    {isValidationErrorModalOpen && (
                        <ValidationErrorModal
                            isOpen={isValidationErrorModalOpen}
                            onClose={handleCloseValidationErrorModal}
                        />
                    )}
                </SimpleForm>
            </Edit>
        </Container>
    );
};
